import { useState, memo, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Divider,
  Paper,
  Snackbar,
  Chip,
  OutlinedInput,
  ListItemText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import NestedFormRow from "../../NestedFormRow";
import {
  getDefaultWebhookAttrs,
  safeParseJSON,
  buildJsonObject,
  jsonToNodes,
} from "../../../utils/functions";

/**
 * Modal to edit an existing External Integration
 * Transforms backend JSON back into visual builder nodes.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {Function} props.onConfirm
 * @param {boolean} props.loading
 * @param {object} props.initialData - Data from the backend to populate the form
 * * @param {Array} props.sellers - Array of available sellers [{ id: 1, name: "Ninja Hubs" }]
 * @returns {JSX.Element}
 */
const EditExternalIntegrationModal = ({
  open,
  onClose,
  onConfirm,
  loading,
  initialData,
  sellers,
}) => {
  const [integrationName, setIntegrationName] = useState("");
  const [integrationType, setIntegrationType] = useState("WMS");
  const [url, setUrl] = useState("");
  const [sellerIds, setSellerIds] = useState([]);
  const [inboundUrl] = useState(
    `${process.env.REACT_APP_URL}exposed_api/webhook/inbound/update_status/`,
  );
  const [token, setToken] = useState("");

  const [configuration, setConfiguration] = useState({
    enable_extra_actions: false,
    enable_inbound: false,
  });

  const [statusMapping, setStatusMapping] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [bodyNodes, setBodyNodes] = useState([]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    if (open && initialData) {
      setIntegrationName(initialData.integration_name || "");
      setIntegrationType(initialData.integration_type || "WMS");
      setUrl(initialData.url || "");
      setSellerIds(initialData.seller_ids || []);
      setToken(initialData.token || "");

      const config = safeParseJSON(initialData.configuration);
      const backendHeaders = safeParseJSON(initialData.headers);
      const backendBody = safeParseJSON(initialData.body);

      setConfiguration({
        enable_extra_actions: config.enable_extra_actions === true,
        enable_inbound: config.enable_inbound === true,
      });

      if (config.status_mapping) {
        const smArray = Object.entries(config.status_mapping).map(
          ([int, ext]) => ({
            id: Date.now().toString() + Math.random(),
            external: ext,
            internal: int,
          }),
        );
        setStatusMapping(smArray);
      } else {
        setStatusMapping([]);
      }

      if (backendHeaders && Object.keys(backendHeaders).length > 0) {
        const headersArray = Object.entries(backendHeaders).map(([k, v]) => ({
          id: Date.now().toString() + Math.random(),
          key: k,
          value: v,
        }));
        setHeaders(headersArray);
      } else {
        setHeaders([]);
      }

      if (backendBody && Object.keys(backendBody).length > 0) {
        setBodyNodes(jsonToNodes(backendBody));
      } else {
        setBodyNodes([]);
      }
    }
  }, [open, initialData]);

  const handleCopyTag = (tag) => {
    navigator.clipboard.writeText(tag);
    setSnackbarOpen(true);
  };

  const handleSellerChange = (event) => {
    const {
      target: { value },
    } = event;
    setSellerIds(typeof value === "string" ? value.split(",") : value);
  };

  const addStatusMappingRow = () => {
    setStatusMapping([
      ...statusMapping,
      { id: Date.now().toString(), external: "", internal: "" },
    ]);
  };

  const updateStatusMapping = (id, field, value) => {
    setStatusMapping(
      statusMapping.map((sm) =>
        sm.id === id ? { ...sm, [field]: value } : sm,
      ),
    );
  };

  const deleteStatusMapping = (id) => {
    setStatusMapping(statusMapping.filter((sm) => sm.id !== id));
  };

  const addHeaderRow = () => {
    setHeaders([...headers, { id: Date.now().toString(), key: "", value: "" }]);
  };

  const updateHeader = (id, field, value) => {
    setHeaders(
      headers.map((h) => (h.id === id ? { ...h, [field]: value } : h)),
    );
  };

  const deleteHeader = (id) => {
    setHeaders(headers.filter((h) => h.id !== id));
  };

  const createNode = () => ({
    id: Date.now().toString() + Math.random(),
    key: "",
    type: "string",
    value: "",
    children: [],
  });

  const addBodyRow = () => {
    setBodyNodes([...bodyNodes, createNode()]);
  };

  const updateBodyNode = useCallback((nodes, id, field, value) => {
    return nodes.map((node) => {
      if (node.id === id) {
        return {
          ...node,
          [field]: value,
          ...(field === "type" && value === "object" ? { value: "" } : {}),
        };
      }
      if (node.children) {
        return {
          ...node,
          children: updateBodyNode(node.children, id, field, value),
        };
      }
      return node;
    });
  }, []);

  const handleUpdateBody = useCallback(
    (id, field, value) => {
      setBodyNodes((prev) => updateBodyNode(prev, id, field, value));
    },
    [updateBodyNode],
  );

  const deleteBodyNode = useCallback((nodes, id) => {
    return nodes
      .filter((node) => node.id !== id)
      .map((node) => {
        if (node.children)
          return { ...node, children: deleteBodyNode(node.children, id) };
        return node;
      });
  }, []);

  const handleDeleteBody = useCallback(
    (id) => {
      setBodyNodes((prev) => deleteBodyNode(prev, id));
    },
    [deleteBodyNode],
  );

  const addChildToNode = useCallback((nodes, parentId) => {
    return nodes.map((node) => {
      if (node.id === parentId)
        return { ...node, children: [...(node.children || []), createNode()] };
      if (node.children)
        return { ...node, children: addChildToNode(node.children, parentId) };
      return node;
    });
  }, []);

  const handleAddChild = useCallback(
    (parentId) => {
      setBodyNodes((prev) => addChildToNode(prev, parentId));
    },
    [addChildToNode],
  );

  const handleConfirm = () => {
    const headersObj = {};
    headers.forEach((h) => {
      if (h.key) headersObj[h.key] = h.value;
    });

    const statusMappingObj = {};
    statusMapping.forEach((sm) => {
      if (sm.external && sm.internal) {
        statusMappingObj[sm.external] = sm.internal;
      }
    });

    const bodyObj = buildJsonObject(bodyNodes);

    const payload = {
      integration_name: integrationName,
      integration_type: integrationType,
      url: url,
      seller_ids: sellerIds,
      token: token,
      inbound_url: inboundUrl,
      configuration: {
        enable_extra_actions: configuration.enable_extra_actions,
        enable_inbound: configuration.enable_inbound,
        status_mapping: statusMappingObj,
      },
      headers: headersObj,
      body: bodyObj,
    };

    onConfirm(payload);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Editar Integración Externa</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary">
                1. Datos Generales
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Proveedor</InputLabel>
                    <Select
                      value={integrationName}
                      label="Proveedor"
                      onChange={(e) => setIntegrationName(e.target.value)}
                      disabled
                    >
                      <MenuItem value="GENERIC">Genérico (Webhook)</MenuItem>
                      <MenuItem value="ALTANET">Altanet</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Sistema</InputLabel>
                    <Select
                      value={integrationType}
                      label="Tipo de Sistema"
                      onChange={(e) => setIntegrationType(e.target.value)}
                      disabled
                    >
                      <MenuItem value="WMS">WMS (Bodega)</MenuItem>
                      <MenuItem value="ERP">ERP (Planificación)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {sellers && (
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="edit-seller-multiple-select-label">
                        Sellers Asignados
                      </InputLabel>
                      <Select
                        labelId="edit-seller-multiple-select-label"
                        multiple
                        value={sellerIds}
                        onChange={handleSellerChange}
                        input={<OutlinedInput label="Sellers Asignados" />}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {selected.map((value) => {
                              const seller = sellers.find(
                                (s) => s.id === value,
                              );
                              return (
                                <Chip
                                  key={value}
                                  label={seller ? seller.name : value}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {sellers.map((seller) => (
                          <MenuItem key={seller.id} value={seller.id}>
                            <Checkbox
                              checked={sellerIds.indexOf(seller.id) > -1}
                            />
                            <ListItemText primary={seller.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="URL de Destino"
                    placeholder="https://..."
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary">
                2. Configuración Interna y Recepción
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={configuration.enable_extra_actions}
                        onChange={(e) =>
                          setConfiguration({
                            ...configuration,
                            enable_extra_actions: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Habilitar acciones extra (triggers automáticos)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={configuration.enable_inbound}
                        onChange={(e) =>
                          setConfiguration({
                            ...configuration,
                            enable_inbound: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Habilitar recepción de información (Inbound)"
                  />
                </Grid>

                {configuration.enable_inbound && (
                  <>
                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, bgcolor: "#f5f5f5", mt: 1, mb: 2 }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          gutterBottom
                        >
                          URL de Recepción Segura
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          mb={1}
                        >
                          Entregue esta URL al proveedor para que notifique los
                          cambios de estado
                        </Typography>

                        <Box display="flex" gap={1}>
                          <TextField
                            fullWidth
                            size="small"
                            value={inboundUrl}
                            disabled
                            InputProps={{
                              style: {
                                backgroundColor: "#e0e0e0",
                                color: "#424242",
                              },
                            }}
                          />
                          <Button
                            sx={{ marginTop: "30px" }}
                            variant="contained"
                            color="secondary"
                            onClick={() => handleCopyTag(inboundUrl)}
                          >
                            Copiar
                          </Button>
                        </Box>
                        <Box display="flex" gap={1}>
                          <TextField
                            fullWidth
                            size="small"
                            value={token}
                            disabled
                            InputProps={{
                              style: {
                                backgroundColor: "#e0e0e0",
                                color: "#424242",
                              },
                            }}
                          />
                          <Button
                            sx={{ marginTop: "30px" }}
                            variant="contained"
                            color="secondary"
                            onClick={() => handleCopyTag(token)}
                          >
                            Copiar
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, bgcolor: "#f5f5f5", mt: 1 }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          <Typography variant="body2" fontWeight="bold">
                            Mapeo de Estados (Interno → Externo)
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={addStatusMappingRow}
                          >
                            Agregar Estado
                          </Button>
                        </Box>

                        {statusMapping.length === 0 && (
                          <Typography variant="caption" color="textSecondary">
                            Agregue mapeos para traducir los estados recibidos
                            del proveedor.
                          </Typography>
                        )}

                        {statusMapping.map((sm) => (
                          <Grid
                            container
                            spacing={1}
                            key={sm.id}
                            sx={{ mb: 1 }}
                            alignItems="center"
                          >
                            <Grid item xs={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Estado Interno</InputLabel>
                                <Select
                                  value={sm.internal}
                                  label="Estado Interno"
                                  onChange={(e) =>
                                    updateStatusMapping(
                                      sm.id,
                                      "internal",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <MenuItem value="fulfilled">
                                    Preparado
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={5}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Estado Externo (Ej: paket)"
                                value={sm.external}
                                onChange={(e) =>
                                  updateStatusMapping(
                                    sm.id,
                                    "external",
                                    e.target.value,
                                  )
                                }
                              />
                            </Grid>
                            <Grid item xs={1}>
                              <IconButton
                                sx={{ marginTop: "30px" }}
                                color="error"
                                size="small"
                                onClick={() => deleteStatusMapping(sm.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        ))}
                      </Paper>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle2" color="primary">
                  3. Cabeceras de la solicitud
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addHeaderRow}
                >
                  Agregar Cabecera
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {headers.map((h) => (
                <Grid container spacing={1} key={h.id} sx={{ mb: 1 }}>
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Ej: Authorization"
                      value={h.key}
                      onChange={(e) =>
                        updateHeader(h.id, "key", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Ej: Bearer token..."
                      value={h.value}
                      onChange={(e) =>
                        updateHeader(h.id, "value", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      sx={{ marginTop: "30px" }}
                      color="error"
                      size="small"
                      onClick={() => deleteHeader(h.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
              {headers.length === 0 && (
                <Typography variant="caption" color="textSecondary">
                  No se han definido headers personalizados.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle2" color="primary">
                  4. Cuerpo de la solicitud
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addBodyRow}
                >
                  Agregar Raíz
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
                {bodyNodes.length === 0 ? (
                  <Typography variant="caption" color="textSecondary">
                    Agregue un nodo raíz para comenzar a construir el JSON.
                  </Typography>
                ) : (
                  bodyNodes.map((node) => (
                    <NestedFormRow
                      key={node.id}
                      node={node}
                      depth={0}
                      onChange={handleUpdateBody}
                      onDelete={handleDeleteBody}
                      onAddChild={handleAddChild}
                    />
                  ))
                )}
              </Paper>
              <Box mt={3}>
                <Typography variant="subtitle2" color="textSecondary" mb={1.5}>
                  Valores disponibles de sistema (Clic para copiar)
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1.5}>
                  {getDefaultWebhookAttrs().map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onClick={() => handleCopyTag(tag)}
                      clickable
                      variant="outlined"
                      sx={{
                        color: "#00bfa5",
                        borderColor: "#e0e0e0",
                        fontWeight: "bold",
                        bgcolor: "#ffffff",
                        "&:hover": {
                          bgcolor: "#e0f2f1",
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={loading || !integrationName || !url}
          >
            {loading ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="Etiqueta copiada al portapapeles"
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      />
    </>
  );
};

export default memo(EditExternalIntegrationModal);
